/**
 * The requisitions the signed-in candidate is hired for (decision 109): the BGV service's list, which decides where
 * the app offers the onboarding form. The pages with job cards (home, job search, Saved Jobs, the resume matches,
 * My Jobs, job details) wrap them in `HiredRequisitionsProvider`, so every card, the detail panel and the home card share
 * one request, and `OnboardingJobButton` reads it. Loaded on mount, never cached across pages. Behind the build gate, so
 * a production build has no import of it.
 */
import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import type { HiredRequisition } from '../../index';
import { ONBOARDING_ENABLED } from '../gating/isOnboardingEnabled';
import { useSignedInUserId } from './useIsSignedIn';

const loadHiredRequisitions = ONBOARDING_ENABLED
  ? (): Promise<HiredRequisition[]> => import('../../index').then((feature) => feature.getHiredRequisitions())
  : null;

const NONE: HiredRequisition[] = [];

/**
 * The candidate's hired requisitions, newest first: `undefined` while they load, none when the feature is off, the
 * candidate is signed out or the list can't be read.
 */
export function useHiredRequisitions(enabled: boolean = ONBOARDING_ENABLED): HiredRequisition[] | undefined {
  const userId = useSignedInUserId();
  const active = userId !== undefined && enabled && loadHiredRequisitions !== null;
  const [hired, setHired] = useState<HiredRequisition[]>();
  useEffect(() => {
    if (!active || !loadHiredRequisitions) return undefined;
    let cancelled = false;
    loadHiredRequisitions().then(
      (loaded) => !cancelled && setHired(loaded),
      () => !cancelled && setHired(NONE), // the chunk didn't load
    );
    return () => {
      cancelled = true;
      setHired(undefined); // another candidate loads their own list
    };
  }, [active, userId]);
  return active ? hired : NONE;
}

/** The display ids of the hired requisitions; empty without a provider */
const HiredDisplayIds = createContext<ReadonlySet<string>>(new Set());

/** The provider's list (`undefined` while it loads); `null` without a provider */
const HiredList = createContext<HiredRequisition[] | undefined | null>(null);

interface HiredRequisitionsProviderProps {
  children: ReactNode;
  /** Defaults to the build gate; tests pass it explicitly */
  enabled?: boolean;
}

/** Loads the hired requisitions once for everything inside it (a page with job cards) */
export function HiredRequisitionsProvider({ children, enabled = ONBOARDING_ENABLED }: HiredRequisitionsProviderProps) {
  const hired = useHiredRequisitions(enabled);
  const displayIds = useMemo(() => new Set((hired ?? NONE).map((requisition) => requisition.requisitionDisplayId)), [hired]);
  return (
    <HiredList.Provider value={hired}>
      <HiredDisplayIds.Provider value={displayIds}>{children}</HiredDisplayIds.Provider>
    </HiredList.Provider>
  );
}

/** The list of the provider around it, so the page loads it once; its own request without one */
export function useSharedHiredRequisitions(enabled: boolean = ONBOARDING_ENABLED): HiredRequisition[] | undefined {
  const shared = useContext(HiredList);
  const own = useHiredRequisitions(enabled && shared === null);
  if (!enabled) return NONE;
  return shared === null ? own : shared;
}

/** Whether the candidate is hired for the job with this display id, by the provider's list */
export function useIsHiredFor(requisitionDisplayId: string | undefined): boolean {
  const displayIds = useContext(HiredDisplayIds);
  return requisitionDisplayId !== undefined && displayIds.has(requisitionDisplayId.trim());
}
